import { ReactElement, useMemo, useEffect, useRef } from 'react';
import Plotly from 'plotly.js-dist-min';
import ChartCard from '../ChartCard';

interface WheelCircumferenceData {
  [manufacturer: string]: {
    [name: string]: {
      [innerWidthMm: string]: {
        [pressure: string]: {
          values: number[];
          average: number;
        };
      };
    };
  };
}

interface TyreReportWheelCircumferencePlotProps {
  wheelCircumferenceData?: WheelCircumferenceData;
  tyres: any[];
  usedTyreIds: Set<string>;
  height?: number;
}

const COLORS = [
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff7c7c',
  '#8dd1e1',
  '#d084d0',
  '#ffb347',
  '#67b7dc',
  '#a4de6c',
  '#ffd93d',
];

export default function TyreReportWheelCircumferencePlot({
  wheelCircumferenceData,
  tyres,
  usedTyreIds,
  height = 500,
}: TyreReportWheelCircumferencePlotProps): ReactElement {
  const plotRef = useRef<HTMLDivElement>(null);

  // Get the tyres that are used in sessions and have wheel circumference data
  const selectedTyres = useMemo(() => {
    const selected = new Set<string>();

    tyres.forEach((tyre) => {
      // Check if this tyre is used in the sessions
      if (!usedTyreIds.has(tyre.id)) {
        return;
      }

      const manufacturerName = tyre.manufacturer?.name || 'Unknown';
      const hasData = wheelCircumferenceData && wheelCircumferenceData[manufacturerName]?.[tyre.name];

      if (hasData) {
        selected.add(`${manufacturerName}|${tyre.name}`);
      }
    });

    return selected;
  }, [tyres, usedTyreIds, wheelCircumferenceData]);

  // Process data for Plotly
  const plotlyData = useMemo(() => {
    const traces: any[] = [];
    let colorIndex = 0;

    if (!wheelCircumferenceData || selectedTyres.size === 0) {
      return [];
    }

    // Process selected tyres data
    selectedTyres.forEach((tyreKey) => {
      const [manufacturer, name] = tyreKey.split('|');

      if (wheelCircumferenceData[manufacturer]?.[name]) {
        const tyreData = wheelCircumferenceData[manufacturer][name];

        // For each inner width
        Object.entries(tyreData).forEach(([innerWidth, pressureData]) => {
          const seriesName = `${manufacturer} ${name} - Rim Width: ${innerWidth}mm`;
          const currentColor = COLORS[colorIndex % COLORS.length];

          // Collect scatter points and average points separately
          const scatterX: number[] = [];
          const scatterY: number[] = [];
          const averageX: number[] = [];
          const averageY: number[] = [];

          Object.entries(pressureData).forEach(([pressure, data]) => {
            const pressureNum = parseFloat(pressure);

            if (data && typeof data === 'object' && data.values && data.average !== undefined) {
              // New format with server-side averaging
              data.values.forEach((value: number) => {
                scatterX.push(pressureNum);
                scatterY.push(value);
              });

              averageX.push(pressureNum);
              averageY.push(data.average);
            } else if (Array.isArray(data)) {
              // Legacy format - array of values
              (data as number[]).forEach((value) => {
                scatterX.push(pressureNum);
                scatterY.push(value);
              });
            } else if (typeof data === 'number') {
              // Single value
              scatterX.push(pressureNum);
              scatterY.push(data);
            }
          });

          // Add scatter trace for individual measurements
          if (scatterX.length > 0) {
            traces.push({
              x: scatterX,
              y: scatterY,
              mode: 'markers',
              type: 'scatter',
              name: `${seriesName} (measurements)`,
              marker: {
                color: currentColor,
                size: 4,
                opacity: 0.6,
              },
              showlegend: false,
            });
          }

          // Add line trace for averages
          if (averageX.length > 0) {
            // Sort average points by x value for proper line drawing
            const sortedData = averageX.map((x, i) => ({ x, y: averageY[i] })).sort((a, b) => a.x - b.x);

            traces.push({
              x: sortedData.map((d) => d.x),
              y: sortedData.map((d) => d.y),
              mode: 'lines+markers',
              type: 'scatter',
              name: seriesName,
              line: {
                color: currentColor,
                width: 3,
              },
              marker: {
                color: currentColor,
                size: 8,
                line: {
                  color: '#fff',
                  width: 2,
                },
              },
            });
          }

          colorIndex++;
        });
      }
    });

    return traces;
  }, [wheelCircumferenceData, selectedTyres]);

  // Update plot when data changes
  useEffect(() => {
    if (plotRef.current && plotlyData.length > 0) {
      const layout = {
        title: {
          text: 'Wheel Circumference vs Pressure',
          font: { family: 'Poppins', size: 16 },
        },
        xaxis: {
          title: { text: 'Pressure (bar)', font: { family: 'Poppins' } },
          type: 'linear',
        },
        yaxis: {
          title: { text: 'Wheel Circumference (m)', font: { family: 'Poppins' } },
        },
        legend: {
          x: 0,
          y: -0.2,
          xanchor: 'left',
          yanchor: 'top',
          orientation: 'v',
          font: { family: 'Poppins', size: 11 },
          tracegroupgap: 5,
        },
        margin: {
          l: 80,
          r: 40,
          t: 60,
          b: 180,
        },
        hovermode: 'closest',
        plot_bgcolor: '#f8f9fa',
        paper_bgcolor: 'white',
        font: { family: 'Poppins' },
        height: height,
      };

      const config = {
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        displaylogo: false,
        responsive: true,
      };

      Plotly.newPlot(plotRef.current, plotlyData, layout, config);
    } else if (plotRef.current && plotlyData.length === 0) {
      Plotly.purge(plotRef.current);
    }

    return () => {
      if (plotRef.current) {
        Plotly.purge(plotRef.current);
      }
    };
  }, [plotlyData, height]);

  if (selectedTyres.size === 0) {
    return (
      <div
        className="cr-flex cr-items-center cr-justify-center cr-rounded-xl cr-bg-gray-50 cr-text-gray-500"
        style={{ height: `${height}px` }}
      >
        No wheel circumference data available for the tyres used in these sessions
      </div>
    );
  }

  if (plotlyData.length === 0) {
    return (
      <div
        className="cr-flex cr-items-center cr-justify-center cr-rounded-xl cr-bg-gray-50 cr-text-gray-500"
        style={{ height: `${height}px` }}
      >
        No wheel circumference measurements available
      </div>
    );
  }

  return (
    <ChartCard height={`${height}px`}>
      <div ref={plotRef} className="cr-h-full cr-w-full" />
    </ChartCard>
  );
}
